"use client";

import { useCallback, useRef, useState } from 'react';
import styles from './collapsible.module.scss';

/**
 * Collapsible content dropdown. Primarily used for listing which guests are at which table.
 * 
 * @param props - the component props
 * @param props.title - The title and what you read when it is closed
 * @param props.content - The content that appears when it opens
 * @returns a collapsible component with animated dropdown
 * 
 * @example
 * ```tsx
 * <Collapsible 
 *  title="Table 1" 
 *  content="John Doe, Jane Doe, and 2 others" 
 * />
 * ```
 */
const Collapsible = ({
    title,
    content,
}: {
    title: string;
    content: string;
}) => {
    // state for toggling the collapsible
    const [toggledState, settoggledState] = useState(false);
    const hiddenContent = useRef<HTMLDivElement>(null);

    // function to toggle the collapsible and memoize it to reduce re-renders
    const toggleCollapsible = useCallback(() => {
        settoggledState((prev) => !prev);
    }, []);

    return (
        <div className={styles.collapseContainer} id={`collapsible-content-${title}`}>
            <button
                type="button"
                onClick={toggleCollapsible}
                className={`${styles.collapsibleButton} ${toggledState ? styles.active : ''}`}
                aria-expanded={toggledState}
                aria-controls={`collapsible-content-${title}`}
            >
                <p className={styles.buttonText}> {title}</p>
            </button>
            <div
                ref={hiddenContent}
                // styles to make smooth collapse animation
                style={
                    toggledState
                        ? { height: `${hiddenContent.current!.scrollHeight}px` }
                        : { height: '0px' }
                }
                className={`${styles.content}`}
                aria-hidden={!toggledState}
            >
                <div
                    className={`${styles.collapsiblePara} gibson-1`}
                    dangerouslySetInnerHTML={{ __html: content }}
                ></div>
            </div>
        </div>
    );
};

export default Collapsible;
